import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import type { SxProps, Theme } from "@mui/material/styles";
import { useState, type KeyboardEvent, type ReactNode } from "react";

/**
 * Which tab each handled key selects. A key absent from here is not ours, so it
 * keeps its default action — that is the whole of the guard in `onKeyDown`, and
 * the reason the `preventDefault` after it reaches these four and nothing else.
 */
const TAB_KEYS: Record<
  string,
  ((count: number, selected: number) => number) | undefined
> = {
  ArrowRight: (count, selected) => (selected + 1) % count,
  ArrowLeft: (count, selected) => (selected - 1 + count) % count,
  Home: () => 0,
  End: (count) => count - 1,
};

/**
 * Every tab group the marketing pages draw, which share their semantics and
 * nothing else.
 *
 * MUI's `Tabs` cannot draw any of them: the dashboard tabs are folder tabs
 * sitting on a rule, the use-case and feature tabs are pills in a tinted tray,
 * the feature overview tabs a rail beside their panel. So this owns the roles,
 * the selection and the keys, and each caller styles its own control. The
 * published widget is a row of anchors whose href is an in-page hash that goes
 * nowhere, which is why these are buttons.
 */
export default function HomeTabs({
  idPrefix,
  labels,
  panels,
  listLabel,
  listSx,
  tabSx,
  panelsWrapperSx,
  panelSx,
}: {
  idPrefix: string;
  labels: ReactNode[];
  panels: ReactNode[];
  listLabel?: string;
  listSx: SxProps<Theme>;
  tabSx: (selected: boolean) => SxProps<Theme>;
  /** The frame the panels share, where a group draws one. */
  panelsWrapperSx?: SxProps<Theme>;
  panelSx?: SxProps<Theme>;
}) {
  const [selected, setSelected] = useState(0);

  /*
    Selection and focus move together. The roving tabIndex follows `selected`,
    so changing it alone would leave the user's focus on a tab that is now
    tabIndex={-1} — silent to a screen reader, and it drops the tablist out of
    the tab sequence on the next Tab press.
  */
  const select = (index: number) => {
    setSelected(index);
    document.getElementById(`${idPrefix}-tab-${index}`)?.focus();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    const tabFor = TAB_KEYS[event.key];
    if (!tabFor) {
      return;
    }
    select(tabFor(labels.length, selected));
    // Home and End otherwise scroll the page out from under the tabs.
    event.preventDefault();
  };

  return (
    <>
      <Box role="tablist" aria-label={listLabel} sx={listSx}>
        {labels.map((label, index) => (
          <ButtonBase
            // The labels are fixed editorial strings, in a list that never
            // reorders, so the index is the identity.
            key={idPrefix + index}
            role="tab"
            id={`${idPrefix}-tab-${index}`}
            aria-controls={`${idPrefix}-panel-${index}`}
            aria-selected={index === selected}
            tabIndex={index === selected ? 0 : -1}
            onClick={() => setSelected(index)}
            onKeyDown={onKeyDown}
            sx={tabSx(index === selected)}
          >
            {label}
          </ButtonBase>
        ))}
      </Box>
      <Box sx={panelsWrapperSx}>
        {panels.map((panel, index) => (
          <Box
            key={idPrefix + index}
            role="tabpanel"
            id={`${idPrefix}-panel-${index}`}
            aria-labelledby={`${idPrefix}-tab-${index}`}
            hidden={index !== selected}
            sx={[
              ...(Array.isArray(panelSx) ? panelSx : [panelSx]),
              index === selected ? false : { display: "none" },
            ]}
          >
            {panel}
          </Box>
        ))}
      </Box>
    </>
  );
}
